import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import IconButton from "../../../../../../../base/button/icon_button";
import {Add, ArrowBack, Remove} from "@mui/icons-material";
import {createUUId} from "../../../../../../../../../core/services/utils/utils";
import Button from "../../../../../../../base/button/button";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnHighlightingMechanisms,
    DataGridColumnTypes,
    useDataGridSavedState
} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {AddPartSupportRequestWarehousesDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import ProductInformationPopover from "../../../../../../../app-specific/draggable-popover/product-information-popover";

const AddPartSupportRequestLookupSectionWarehousesSection = ({
                                                                 defineValues,
                                                                 lookupValues,
                                                                 setLookupValues,
                                                                 previousSection,
                                                             }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const isSerialized = useMemo(() => !!lookupValues?.isSerialized ?? true, [lookupValues?.isSerialized]);

    const dataGridSaveConfig = useMemo(() =>
            isSerialized
                ? ({
                    name: 'AddPartSupportRequestLookupSectionWarehousesSectionSerializedDataGrid',
                    version: 1,
                })
                : ({
                    name: 'AddPartSupportRequestLookupSectionWarehousesSectionUnSerializedDataGrid',
                    version: 1,
                })
        , [isSerialized])

    const columns = useMemo(() => {
        /**@type {DataGridColumn[]}*/
        const columns = [
            {
                title: 'Warehouse',
                name: AddPartSupportRequestWarehousesDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Part No',
                name: AddPartSupportRequestWarehousesDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Serial No',
                name: AddPartSupportRequestWarehousesDataGridColumnNames.serialNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                sortable: false,
                width: 150
            },
            {
                title: 'Selected Qty',
                name: AddPartSupportRequestWarehousesDataGridColumnNames.selectedQty,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                name: AddPartSupportRequestWarehousesDataGridColumnNames.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
        ];
        if (isSerialized) {
            return columns.filter(e => ![
                AddPartSupportRequestWarehousesDataGridColumnNames.selectedQty,
            ].includes(e.name));
        } else {
            return columns.filter(e => ![
                AddPartSupportRequestWarehousesDataGridColumnNames.serialNo,
                AddPartSupportRequestWarehousesDataGridColumnNames.select,
            ].includes(e.name));
        }
    }, [isSerialized])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * With each change in the part of the lookup values:
     * - searches for the warehouse entries of the given part.
     */
    useEffect(() => {
        if (!lookupValues?.part) return;
        getWarehouseParts().then();
    }, [lookupValues?.part])


    /**
     * Fetches a list of parts that exists in the provided warehouse form the server.
     * * if the response of the api is successful, sets the state list to populate the tables.
     */
    const getWarehouseParts = async () => {
        setLoading(true);
        const forApi = {
            partNo: lookupValues.part.partNumber,
            warehouseCode: defineValues.warehouse.code,
        }
        const response = await Api.getPartSerialNosAndQuantityByWarehouse(forApi)
        if (!isMounted())
            return;
        const res = [];
        if (response?.resultFlag) {
            // if serialized
            if (!!response?.data?.serials) {
                response?.data?.serials?.forEach(serialNo => {
                    res.push({
                        id: createUUId(),
                        partNo: lookupValues.part.partNumber,
                        warehouse: defineValues.warehouse.name,
                        warehouseCode: defineValues.warehouse.code,
                        serialNo: serialNo,
                    })
                })
            } else {
                res.push({
                    id: createUUId(),
                    partNo: lookupValues.part.partNumber,
                    warehouse: defineValues.warehouse.name,
                    warehouseCode: defineValues.warehouse.code,
                    availableQty: response?.data?.availableQty ?? 0,
                    selectedQuantity: 0,
                })
            }
        }
        setLookupValues(prevState => ({
            ...prevState,
            isSerialized: !!response?.data?.serials
        }));
        setData(res);
        setLoading(false);
    }


    /**
     * If the serialNo of the warehouse is the same as the serial number of lookup values, then removes it, otherwise
     * selects it.
     * @param entry
     */
    const selectOrRemoveWarehouseEntryForSubmission = (entry) => {
        const selected = isSerialized
            ? entry.serialNo === lookupValues?.serialNumber
            : entry?.selectedQuantity <= 0;
        if (selected) {
            return setLookupValues(prevState => ({
                ...prevState,
                serialNumber: null,
                invoiceNo: null,
                warehouseCode: null,
                selectedQuantity: null,
            }));
        }
        if (isSerialized) {
            return setLookupValues(prevState => ({
                ...prevState,
                selectedQuantity: null,
                serialNumber: entry.serialNo,
                invoiceNo: null,
                warehouseCode: entry?.warehouseCode,
            }))
        }
        setLookupValues(prevState => ({
            ...prevState,
            selectedQuantity: entry.selectedQuantity?.toString(),
            serialNumber: null,
            invoiceNo: null,
            warehouseCode: entry?.warehouseCode,
        }))
    }

    /**
     * Changes the selected quantity of the given entry with the newValue.
     *
     * @param newValue {number}
     * @param entry {any}
     */
    const changeSelectedQuantity = (newValue, entry) => {
        const selectedQuantity = !newValue
            ? null
            : Math.max(
                0,
                Math.min(
                    parseFloat(entry.availableQty ?? '0'),
                    newValue
                )
            );
        setData(prevState => prevState?.map(e => e.id === entry.id
            ? {...e, selectedQuantity: selectedQuantity}
            : e
        ));
        selectOrRemoveWarehouseEntryForSubmission({
            ...entry,
            selectedQuantity: selectedQuantity
        });
    }

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data?.map(entry => {
            const selected = isSerialized
                ? entry.serialNo === lookupValues?.serialNumber
                : false;
            return ({
                key: entry?.invoiceNo,
                data: entry,
                cells: {
                    [AddPartSupportRequestWarehousesDataGridColumnNames.warehouse]: entry.warehouse,
                    [AddPartSupportRequestWarehousesDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={entry?.partNo}>
                            {entry.partNo ?? ""}
                        </ProductInformationPopover>
                    ,
                    [AddPartSupportRequestWarehousesDataGridColumnNames.serialNo]: entry.serialNo,
                    [AddPartSupportRequestWarehousesDataGridColumnNames.selectedQty]:
                        <div className='d-flex w-100 align-items-center justify-content-center'>
                            <IconButton
                                className={''}

                                onPressed={() => changeSelectedQuantity((entry.selectedQuantity ?? 0) - 1, entry)}
                                color={'secondary'}
                            >
                                <Remove className={'icon small'}/>
                            </IconButton>
                            <p {...dataGridCellTextProps} className={'text-sm mx-4 secondary-color-1 font-weight-semi-bold'}>
                                        <span className={'text-color-3'}>
                                            {entry.selectedQuantity ?? 0}
                                        </span> / {entry.availableQty ?? 0}
                            </p>
                            <IconButton

                                onPressed={() => changeSelectedQuantity((entry.selectedQuantity ?? 0) + 1, entry)}
                                color={'secondary'}
                            >
                                <Add className={'icon small'}/>
                            </IconButton>
                        </div>,
                    [AddPartSupportRequestWarehousesDataGridColumnNames.select]:
                        <Button
                            variant={selected ? 'contained' : 'outlined'}
                            className={'px-4 py-1 font-weight-semi-bold'}
                            onClick={() => selectOrRemoveWarehouseEntryForSubmission(entry)}
                        >
                            {
                                selected
                                    ? "Remove"
                                    : "Select"
                            }
                        </Button>,
                },
            })
        })
    }, [data, isSerialized, lookupValues?.serialNumber])

    return (
        <>
            <div className={'d-flex flex-wrap px-4 mb-3'}>
                <div className={'d-flex align-items-start justify-content-start w-100'}>
                    <IconButton
                        onPressed={previousSection}
                        color={'secondary'}
                        style={{marginTop: 2}}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <div className={'d-flex flex-column'}>
                        <ProductInformationPopover
                            partNo={lookupValues?.part?.partNumber}
                            wrapperProps={{className: 'ml-3'}}
                            buttonProps={{className: ' pl-0'}}
                        >
                            <span className={'text-md'}>
                                {lookupValues?.part?.partNumber ?? '--'}
                            </span>
                        </ProductInformationPopover>
                        <p className={'text-sm grey-3 ml-3'}>
                            Back to lookup
                        </p>
                    </div>
                </div>
            </div>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                rows={dataGridRows}
                columns={dataGridColumns}
                hideExporting
                hideFooterActions
                hideFooter
                visibleRows={5}
                state={{
                    loading: {state: loading},
                    density: dataGridDensity,
                }}
            />
        </>
    );
}


export default AddPartSupportRequestLookupSectionWarehousesSection;
